import type { ReactElement } from "react";
import type { ApiClient, Kostenstelle } from "@kundeninfo/api-client";
import { Feld } from "@kundeninfo/ui";
import { Schalter } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../../components/meHuellen.js";
import { Loaded, MasterDataPanel, MasterDataTable, StateFlags, useMasterData } from "./masterData.js";
import { errorProp } from "../../../components/fieldErrors.js";
import { ACCOUNTS_TEXT as T } from "./texts.js";

/**
 * Verwaltung › KOSTENSTELLEN (Spec „Kostenstellen“, E364, Entscheid 494): die des aktiven Unternehmens samt
 * deaktivierten; neue entstehen dort. Das Kürzel (DATEV KOST1, höchstens 8 Zeichen aus `[A-Z0-9-]`) wird beim Tippen
 * groß geschrieben und steht nach dem Anlegen fest (P22).
 */
export type CostCentresClient = Pick<ApiClient, "getCostCentres" | "createCostCentre" | "updateCostCentre">;

type CostCentreDraft = { id: string | null; version: number; code: string; name: string; active: boolean };

const CODE_MAX = 8;
const LABELS = { code: T.code, name: T.name, active: T.active };

const toDraft = (c: Kostenstelle | null): CostCentreDraft => (c === null
  ? { id: null, version: 0, code: "", name: "", active: true }
  : { id: c.id, version: c.version, code: c.code, name: c.name, active: c.active });

export function CostCentresSection(p: { client: CostCentresClient; companyId: string | null }): ReactElement {
  const { client, companyId } = p;
  const m = useMasterData(() => client.getCostCentres({ ...(companyId === null ? {} : { companyId }), includeInactive: true }),
    companyId ?? "", toDraft);
  const d = m.draft;

  async function save(): Promise<void> {
    if (d === null) return;
    if (d.id === null) await client.createCostCentre({ code: d.code, name: d.name, active: d.active });
    else await client.updateCostCentre(d.id, { version: d.version, name: d.name, active: d.active });
    await m.reload();
  }

  return (
    <div className="vwk">
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => m.open(null)}>{T.newCostCentre}</KiKnopf>
      </div>
      <Loaded list={m}>{(rows) => (
        <MasterDataTable rows={rows} onEdit={m.open} editName={(c) => T.editNamed(`${c.code} ${c.name}`)}
          columns={[
            { head: T.code, cell: (c) => c.code },
            { head: T.name, cell: (c) => c.name },
            { head: T.state, cell: (c) => <StateFlags row={c} /> },
          ]} />
      )}</Loaded>

      {d !== null && (
        <MasterDataPanel title={d.id === null ? T.newCostCentre : T.editCostCentre} kind={T.costCentreKind} name={d.name} dirty={m.dirty} deactivating={m.deactivating}
          save={save} refresh={m.refresh} onClose={m.close} labels={LABELS}>
          {(f) => (
            <>
              <Feld id="vwk-code" label={T.code} bearbeitbar={d.id === null} pflicht max={CODE_MAX} wert={d.code}
                fehler={f.code} onChange={(w) => m.change({ code: w.toUpperCase() })} />
              <Feld id="vwk-name" label={T.name} bearbeitbar pflicht wert={d.name} fehler={f.name}
                onChange={(w) => m.change({ name: w })} />
              <Schalter beschriftung={T.active} an={d.active} {...errorProp(f.active)}
                onGeaendert={(e) => m.change({ active: e.an })} />
            </>
          )}
        </MasterDataPanel>
      )}
    </div>
  );
}
